import { Button, Col, Form, Input, Row, Skeleton, Switch } from "antd";
import _isEmpty from "lodash/isEmpty";
import _map from "lodash/map";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import AppModal from "../../components/AppModal/AppModal";
import CustomSelect from "../../components/CustomSelect";
import {
  brokerAssociatedPropertiesSuccess,
  createBroker,
  getBrokerAssociatedProperties,
  getBrokerById,
  getBrokerByIdSuccess,
  updateBroker,
} from "../../store/actions/broker.actions";
import { getActiveMarkets } from "../../store/actions/market.actions";
import { SEARCH_QUERY, USER_ROLES, VALIDATE_FORM_MESSAGES_TEMPLATE } from "../../utils/constants";
import permissionsUtil from "../../utils/permissions.util";
import { getFeatureFlags, isValidPhoneNumber, normalizeLatinLetters, populateDisplayName } from "../../utils/utils";
import "./AddEditBroker.scss";

const queryParam = { filterCriteria: { isActive: true }, sort: "id,asc" };

const INITIALS_VALIDATION_MESSAGES = {
  required: "Initials required",
  length: "Initials must be 2–3 letters.",
  max: "Max 3 letters",
  letters: "Initials must contain only letters",
};

const getInitialsValidationMessage = value => {
  if (!value) return INITIALS_VALIDATION_MESSAGES.required;
  if (!/^[A-Z]+$/.test(value)) return INITIALS_VALIDATION_MESSAGES.letters;
  if (value.length < 2) return INITIALS_VALIDATION_MESSAGES.length;
  if (value.length > 3) return INITIALS_VALIDATION_MESSAGES.max;
  return null;
};

const deriveBrokerInitials = values => {
  const firstName = normalizeLatinLetters(values.firstName);
  const lastName = normalizeLatinLetters(values.lastName);

  if (firstName && lastName) {
    return `${firstName[0]}${lastName[0]}`.toUpperCase();
  }

  const fallbackName = firstName || lastName;
  if (fallbackName.length >= 2) {
    return fallbackName.slice(0, 3).toUpperCase();
  }

  return "";
};

const AddEditBroker = ({ brokerId, onSuccess, onCloseModal }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { value: broker, loading, associatedProperties } = useSelector(store => store.broker);
  const { activeList: markets, loading: fetchingMarkets } = useSelector(store => store.market);
  const { value: featureFlags = {}, loaded: featureFlagsLoaded = false } = useSelector(
    store => store.featureFlag || {},
  );

  const [form] = Form.useForm();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [fetchingBroker, setFetchingBroker] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const FEATURE_FLAG = featureFlagsLoaded ? featureFlags : getFeatureFlags() || {};
  const { property_search_redesign: propertySearchRedesign = { status: false } } = FEATURE_FLAG;
  const isPropertySearchRedesignEnabled = propertySearchRedesign.status;

  const canEditUser = permissionsUtil.checkAuth("user_edit");
  const canEditInitials = permissionsUtil.hasRole(USER_ROLES.ADMIN) || permissionsUtil.hasRole(USER_ROLES.EDITOR);
  const propertyCodes = _map(associatedProperties, "id")
    .filter(Boolean)
    .join(", ");

  const onFormFinish = async fieldValues => {
    const _fieldValues = { ...fieldValues, markets: _map(fieldValues.markets, "value") };
    if (!canEditInitials) {
      delete _fieldValues.initials;
    }

    if (brokerId) {
      delete _fieldValues.id;
      await dispatch(updateBroker(brokerId, _fieldValues));
    } else {
      _fieldValues.isActive = _fieldValues.isActive || false;
      await dispatch(createBroker(_fieldValues));
    }
    onCloseModal(false);
    if (onSuccess) {
      onSuccess(brokerId);
    }
  };

  useEffect(() => {
    const didMount = async () => {
      fetchMarkets();
      if (brokerId) {
        setIsEditMode(true);
        setFetchingBroker(true);
        dispatch(getBrokerAssociatedProperties(brokerId, true));
        await dispatch(getBrokerById(brokerId));
        setFetchingBroker(false);
      } else {
        form.setFieldsValue({ isActive: true });
      }
    };
    didMount();
  }, []);

  useEffect(
    () => () => {
      dispatch(brokerAssociatedPropertiesSuccess([]));
      form.resetFields();
    },
    [],
  );

  useEffect(() => {
    if (broker) {
      form.setFieldsValue({
        ...broker,
        initials: broker.initials || deriveBrokerInitials(broker),
        markets: _map(broker?.markets, m => ({
          ...m,
          label: m.id,
          value: m.id,
        })),
      });
    }
  }, [broker]);

  const fetchMarkets = async query => {
    let _query = query;
    if (!_query) {
      _query = { ...SEARCH_QUERY, ...queryParam };
      _query.size = 500;
    }
    dispatch(getActiveMarkets(_query));
  };

  const updateDisplayName = () => {
    populateDisplayName(form);
  };

  const updateInitials = values => {
    form.setFieldsValue({ initials: deriveBrokerInitials(values) });
    if (isPropertySearchRedesignEnabled && canEditInitials) {
      form.validateFields(["initials"]).catch(() => {});
    }
  };

  const onFormValuesChange = (changedValues, allValues) => {
    if (changedValues.firstName !== undefined || changedValues.lastName !== undefined) {
      updateInitials(allValues);
    }

    if (
      changedValues.firstName !== undefined ||
      changedValues.lastName !== undefined ||
      changedValues.email !== undefined ||
      changedValues.phone !== undefined
    ) {
      updateDisplayName();
    }
  };

  const _onChangeStatus = status => {
    if (!status && !_isEmpty(associatedProperties)) {
      setIsModalOpen(true);
    }
  };

  const _onCloseModal = () => {
    setIsModalOpen(false);
    form.setFieldsValue({ isActive: true });
  };

  const _onConfirm = () => {
    setIsModalOpen(false);
    form.setFieldsValue({ isActive: false });
  };

  return (
    <div className="edit-broker">
      <Skeleton loading={fetchingBroker} active paragraph={{ rows: 6 }}>
        <Form
          form={form}
          className="edit-broker"
          layout="vertical"
          name="nest-messages"
          initialValues={broker}
          onFinish={onFormFinish}
          onValuesChange={onFormValuesChange}
          validateMessages={VALIDATE_FORM_MESSAGES_TEMPLATE}
        >
          <Row className="fields-row" gutter={20} type="flex">
            <Col className={!isEditMode ? "hidden" : ""} xs={24} sm={12} md={8} lg={8}>
              <Form.Item label={t("User.DisplayName")} name="displayName">
                <Input placeholder={t("User.DisplayName")} disabled />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={8}>
              <Form.Item label={t("User.FirstName")} name="firstName" rules={[{ required: true, whitespace: true }]}>
                <Input placeholder={t("User.FirstName")} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={8}>
              <Form.Item label={t("User.LastName")} name="lastName" rules={[{ required: true, whitespace: true }]}>
                <Input placeholder={t("User.LastName")} />
              </Form.Item>
            </Col>
            {isPropertySearchRedesignEnabled && (
              <Col xs={24} sm={12} md={8} lg={8}>
                <Form.Item
                  label={t("AddEditBroker.Initials")}
                  name="initials"
                  validateFirst
                  validateTrigger="onChange"
                  getValueFromEvent={event => event.target.value.toUpperCase()}
                  rules={
                    canEditInitials
                      ? [
                        {
                          required: true,
                          message: INITIALS_VALIDATION_MESSAGES.required,
                        },
                        {
                          validator: (_, value) => {
                            const validationMessage = getInitialsValidationMessage(value);
                            return validationMessage
                              ? Promise.reject(new Error(validationMessage))
                              : Promise.resolve();
                          },
                        },
                      ]
                      : []
                  }
                >
                  <Input disabled={!canEditInitials} />
                </Form.Item>
              </Col>
            )}
            <Col xs={24} sm={12} md={8} lg={8}>
              <Form.Item label={t("User.JobTitle")} name="jobTitle" rules={[{ required: true, whitespace: true }]}>
                <Input placeholder={t("User.JobTitle")} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={8}>
              <Form.Item label={t("User.Email")} name="email" rules={[{ required: true, type: "email" }]}>
                <Input placeholder={t("User.Email")} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={8}>
              <Form.Item
                label={t("AddEditBroker.Phone")}
                name="phone"
                validateFirst
                rules={[
                  {
                    whitespace: true,
                  },
                  { validator: isValidPhoneNumber },
                ]}
              >
                <Input placeholder={t("AddEditBroker.Phone")} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={8}>
              <Form.Item label={t("User.Company")} name="company" rules={[{ required: true, whitespace: true }]}>
                <Input placeholder={t("User.Company")} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={8}>
              <Form.Item label={t("User.LicenseNumber")} name="licenseNumber">
                <Input placeholder={t("User.LicenseNumber")} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={8}>
              <Form.Item label={t("User.IsActive")} hidden={!canEditUser} name="isActive" valuePropName="checked">
                <Switch onChange={_onChangeStatus} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item shouldUpdate label={t("User.MarketLabel")} name="markets" rules={[{ required: true }]}>
                <CustomSelect
                  isMulti
                  placeholder={t("User.MarketPlaceholder")}
                  loading={fetchingMarkets}
                  fetchOptions={fetchMarkets}
                  optionsList={markets}
                  // renderOptions={value => value.id}
                  queryParam={queryParam}
                />
              </Form.Item>
            </Col>
            {isPropertySearchRedesignEnabled && (
              <Col span={24}>
                <Form.Item label={t("AddEditBroker.PropertiesListed")}>
                  <Input value={propertyCodes} disabled />
                </Form.Item>
              </Col>
            )}
            <Col span={24}>
              <Row className="fields-row" gutter={20} type="flex" justify="center">
                <Col xs={12} sm={12} md={6} lg={6}>
                  <Button
                    block
                    onClick={() => {
                      onCloseModal(false);
                      dispatch(getBrokerByIdSuccess({}));
                    }}
                  >
                    {t("User.DiscardChanges")}
                  </Button>
                </Col>
                <Col xs={12} sm={12} md={6} lg={6}>
                  <Form.Item shouldUpdate noStyle>
                    {() => (
                      <Button
                        block
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                        disabled={
                          isPropertySearchRedesignEnabled &&
                          canEditInitials &&
                          !!getInitialsValidationMessage(form.getFieldValue("initials"))
                        }
                      >
                        {brokerId ? t("User.SaveChanges") : t("Buttons.Create")}
                      </Button>
                    )}
                  </Form.Item>
                </Col>
              </Row>
            </Col>
          </Row>
        </Form>
      </Skeleton>
      <AppModal
        centered
        wrapClassName="modal-associated-properties"
        title="Warning"
        width={600}
        minHeight="20vh"
        maxHeight="60vh"
        overFlowY="auto"
        visible={isModalOpen}
        onCancel={_onCloseModal}
        footer={
          <Row justify="center" gutter={24}>
            <Col xs={12} sm={12} md={8} lg={8}>
              <Button block disabled={loading} onClick={_onCloseModal}>
                {t("Buttons.Cancel")}
              </Button>
            </Col>
            <Col xs={12} sm={12} md={8} lg={8}>
              <Button block type="primary" onClick={_onConfirm} loading={loading}>
                {t("Buttons.Confirm")}
              </Button>
            </Col>
          </Row>
        }
        destroyOnClose
      >
        <p>{t("AddEditBroker.Description")}</p>
        <Row gutter={[8, 8]} justify="center">
          {_map(associatedProperties, val => (
            <Col key={val.id} xs={24} sm={24} md={12} lg={12} xl={6} xxl={6} className="associated-property">
              {val.id}
            </Col>
          ))}
        </Row>
      </AppModal>
    </div>
  );
};

AddEditBroker.propTypes = {
  brokerId: PropTypes.string,
  onSuccess: PropTypes.func,
  onCloseModal: PropTypes.func,
};

export default AddEditBroker;
